import axios from "axios";
import { regenerateTokens, userLogout } from "../../../apis/pvlapis";
import { getAccessTokenCookie } from "../../../apis/tokenhandler";

const baseURL = process.env.REACT_APP_PVL_APP_URL;
const apiKey = process.env.REACT_APP_PVL_API_KEY;

const apiClient = axios.create({
  baseURL: baseURL,
  timeout: 100000,
  headers: {
    Accept: "application/json",
    "X-API-Key": apiKey,
  },
});

apiClient.interceptors.request.use(
  function (config) {
    const token = getAccessTokenCookie();
    if (token && token.split(".").length === 3) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 457) &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const status = await regenerateTokens();
        if (status === 200) {
          const token = getAccessTokenCookie();
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          processQueue(null, token);
          return axios(originalRequest);
        } else {
          await userLogout();
        }
      } catch (err) {
        processQueue(err, null);
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

const handleResponse = (response) =>
  response.data ? JSON.parse(JSON.stringify(response.data)) : response.data;

const handleError = (error) => {
  console.error("API call error:", error);
  throw error;
};

const BillingService = {
  getCards: async () => {
    try {
      const response = await apiClient.get(`/pvl/get_user_cards`);
      return handleResponse(response);
    } catch (error) {
      handleError(error);
    }
  },
  setDefaultCard: async (paymentId) => {
    try {
      const response = await apiClient.post(`/pvl/change_default_card`, {
        paymentid: paymentId,
      });
      return handleResponse(response);
    } catch (error) {
      handleError(error);
    }
  },
  deleteCard: async (paymentId) => {
    try {
      const response = await apiClient.post(`/pvl/delete_credit_card`, {
        paymentid: paymentId,
      });
      return handleResponse(response);
    } catch (error) {
      handleError(error);
    }
  },
  addNewCard: async (stripeToken, isDefaultCard) => {
    try {
      const response = await apiClient.post(
        `/pvl/attach_payment_method_to_customer`,
        {
          payment_token: stripeToken,
          flag_set_card_to_default: isDefaultCard,
        }
      );
      return handleResponse(response);
    } catch (error) {
      handleError(error);
    }
  },
};

export default BillingService;
